// src/lib/dpcLevels.ts (2026-09-28)
// DPC 14 桁の階層メタ。DPC分析の階層タブ・主表の見出し・選択中バー (パンくず) で共有する。
//   DPC 14 桁 = 疾患(1-6) + 病態等分類(7) + 年齢・出生時体重・JCS等(8) + 手術(9-10)
//             + 手術・処置等1(11) + 手術・処置等2(12) + 副傷病(13) + 重症度等(14)
//   「x」はその桁で分岐しない (その疾患ではその区分を使わない) ことを表す。
import type { DiseaseRankingRow } from "@shared/api";

export interface DpcLevelMeta {
  v: number;          // 階層 = コードの桁数
  tab: string;        // タブ名
  sub: string;        // タブの補足 (桁)
  crumb: string;      // パンくずの見出し
  desc: string;       // ホバー説明
  segStart: number;   // この階層で増える桁の開始位置 (0 始まり)
  /** 主表の行から、この階層の分岐名を取り出す */
  name: (r: DiseaseRankingRow) => string;
}

export const DPC_LEVEL_META: DpcLevelMeta[] = [
  { v: 6,  tab: "疾患別",   sub: "6桁",   crumb: "疾患",   segStart: 0,
    desc: "DPC 上 6 桁 = 疾患 (MDC + 分類)", name: (r) => r["疾患名"] },
  { v: 7,  tab: "病態等",   sub: "7桁目", crumb: "病態等", segStart: 6,
    desc: "7 桁目 = 病態等分類 (例: 肺炎等の 市中肺炎/院内肺炎・15歳未満)。多くの疾患は x (分岐なし)",
    name: (r) => r["病態等"] },
  { v: 8,  tab: "年齢・JCS等", sub: "8桁目", crumb: "年齢・JCS等", segStart: 7,
    desc: "8 桁目 = 年齢・出生時体重・JCS 等 (例: くも膜下出血・脳梗塞の JCS10未満/10以上)",
    name: (r) => r["年齢等"] },
  { v: 10, tab: "手術別",   sub: "9-10桁", crumb: "手術",  segStart: 8,
    desc: "9-10 桁目 = 手術 (99 = 手術なし / 97 = その他の手術あり)", name: (r) => r["手術名"] },
  { v: 11, tab: "処置1別",  sub: "11桁目", crumb: "処置1", segStart: 10,
    desc: "11 桁目 = 手術・処置等1", name: (r) => r["処置1名"] },
  { v: 12, tab: "処置2別",  sub: "12桁目", crumb: "処置2", segStart: 11,
    desc: "12 桁目 = 手術・処置等2", name: (r) => r["処置2名"] },
  { v: 13, tab: "副傷病",   sub: "13桁目", crumb: "副傷病", segStart: 12,
    desc: "13 桁目 = 定義副傷病の有無", name: (r) => r["副傷病"] },
  { v: 14, tab: "全14桁",   sub: "重症度等", crumb: "重症度等", segStart: 13,
    desc: "14 桁目 = 重症度等。14 桁が確定する階層で、DPC 包括点数を表示", name: (r) => r["重症度"] },
];

export const dpcLevelMeta = (v: number): DpcLevelMeta =>
  DPC_LEVEL_META.find((m) => m.v === v) ?? DPC_LEVEL_META[0];

/** コードのうち、階層 v で増える桁 (例: v=8 → 8 桁目 1 文字)。 */
export function dpcSegment(code: string, v: number): string {
  const m = dpcLevelMeta(v);
  return code.slice(m.segStart, v);
}

/** その階層の桁が全部 x (= この疾患では分岐しない) か。 */
export function isNoBranchSegment(code: string, v: number): boolean {
  if (v === 6) return false;
  const seg = dpcSegment(code, v);
  return seg.length === 0 || /^[xX]+$/.test(seg);
}
